import { Injectable } from '@angular/core';
import { Observable, iif } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';
import { LoadingWrapper } from '../../../decorators';
import { CriteriaToRequestService } from '../../../services/criteria-to-request.service';
import { BuyerPersonasApiService } from '../../../services/api';
import { HubspotBuyerPersonaCreateRequestDto } from '../../../dto';

export const CREATE_EDIT_BUYER_PERSONA_LOADING_TAG: string = 'create-edit-buyer-persona-group-loading-tag';

@UntilDestroy()
@Injectable()
export class CreateEditBuyerPersonaGroupService {
  constructor(
    private readonly criteriaToRequestService: CriteriaToRequestService,
    private readonly buyerPersonasApiService: BuyerPersonasApiService,
    private readonly toastrService: ToastrService,
  ) {
  }

  criteriaToRequest(criteria: any): any {
    const request = {
      ...(criteria?.job_title?.length ? { job_title: criteria.job_title } : {}),
      ...(criteria?.job_sub_departments?.length ? { job_sub_departments: criteria.job_sub_departments } : {}),
    };

    this.criteriaToRequestService.transformTruthMapCsv(criteria, request, ['job_levels', 'job_departments']);

    return request;
  }

  invertTransformTruthMapCsv(
    request: Record<string, string | undefined>,
    keyArray: string[],
    template?: Record<string, Record<string, boolean>>
  ): Record<string, Record<string, boolean>> {
    const result: Record<string, Record<string, boolean>> = {};

    keyArray.forEach(field => {
      const csv = request[field] ?? '';
      const items = csv.length > 0
        ? csv.split(',').map(s => s.trim()).filter(s => s)
        : [];

      const truthMap: Record<string, boolean> = {};

      if (template && template[field]) {
        Object.keys(template[field]).forEach(k => {
          truthMap[k] = items.includes(k);
        });
      } else {
        items.forEach(k => {
          truthMap[k] = true;
        });
      }

      result[field] = truthMap;
    });

    return result;
  }

  saveBuyerPersona(id: number | null, name: string, query: any): Observable<any> {
    const request: HubspotBuyerPersonaCreateRequestDto = {
      filterName: name,
      query,
      source: 'ENRICHMENT',
    };

    return iif(
      () => !!id,
      this.updateBuyerPersona(id, request),
      this.createBuyerPersona(request)
    ).pipe(
      take(1),
      tap(() => this.toastrService.success(this.getSuccessMessage(id))),
      untilDestroyed(this),
    );
  }

  private getSuccessMessage(id: number | null): string {
    return `Buyer persona group ${id ? 'updated' : 'created'} successfully`;
  }

  @LoadingWrapper(CREATE_EDIT_BUYER_PERSONA_LOADING_TAG)
  private createBuyerPersona(request: HubspotBuyerPersonaCreateRequestDto): Observable<any> {
    return this.buyerPersonasApiService.createBuyerPersona(request);
  }

  @LoadingWrapper(CREATE_EDIT_BUYER_PERSONA_LOADING_TAG)
  private updateBuyerPersona(id: number, request: HubspotBuyerPersonaCreateRequestDto): Observable<any> {
    return this.buyerPersonasApiService.updateBuyerPersona(id, request);
  }
}
